import { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Form, Button, Table, Dropdown } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import CustomNavbar from './NavBar';
import '../styles/styles.css';

const NotificationSettings = () => {
  const [settings, setSettings] = useState<any[]>([]);
  const [availableActions, setAvailableActions] = useState<any[]>([]);
  const [newAction, setNewAction] = useState<string>('');
  const [newMessageTemplate, setNewMessageTemplate] = useState<string>('');
  const [newIsEnabled, setNewIsEnabled] = useState<boolean>(false);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchSettings();
  }, [token, navigate]);

  const fetchSettings = () => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/notificationsettings`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setSettings(res.data.settings);
        setAvailableActions(res.data.availableActions);
      })
      .catch((err) => {
        console.error('Failed to fetch settings:', err);
        alert('Failed to load notification settings.');
      });
  };

  const handleUpdate = (id: number, messageTemplate: string, isEnabled: boolean) => {
    axios
      .put(
        `${process.env.REACT_APP_API_BASE_URL}/api/notificationsettings/${id}`,
        { messageTemplate, isEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setSettings(
          settings.map((s) =>
            s.id === id ? { ...s, messageTemplate, isEnabled } : s
          )
        );
      })
      .catch((err) => {
        console.error('Failed to update setting:', err);
        alert('Failed to update notification setting.');
      });
  };

  const handleCreate = () => {
    if (!newAction) {
      alert('Please select an action.');
      return;
    }
    const selectedAction = availableActions.find(
      (a) => a.actionName === newAction
    );
    if (!selectedAction) {
      alert('Invalid action selected.');
      return;
    }
    axios
      .post(
        `${process.env.REACT_APP_API_BASE_URL}/api/notificationsettings`,
        {
          actionName: selectedAction.actionName,
          displayName: selectedAction.displayName,
          messageTemplate: newMessageTemplate,
          isEnabled: newIsEnabled,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setSettings([
          ...settings,
          {
            id: res.data.id,
            actionName: selectedAction.actionName,
            displayName: selectedAction.displayName,
            messageTemplate: newMessageTemplate,
            isEnabled: newIsEnabled,
          },
        ]);
        setNewAction('');
        setNewMessageTemplate('');
        setNewIsEnabled(false);
      })
      .catch((err) => {
        console.error('Failed to create setting:', err);
        alert(
          err.response?.data || 'Failed to create notification setting.'
        );
      });
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Are you sure you want to delete this setting?')) return;
    axios
      .delete(`${process.env.REACT_APP_API_BASE_URL}/api/notificationsettings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setSettings(settings.filter((s) => s.id !== id));
      })
      .catch((err) => {
        console.error('Failed to delete setting:', err);
        alert('Failed to delete notification setting.');
      });
  };

  if (!token) {
    return (
      <Container className="mt-5 text-center">
        <h3 className="text-light animate__animated animate__pulse">
          Please log in to continue
        </h3>
      </Container>
    );
  }

  return (
    <>
      <CustomNavbar />
      <Container fluid className="mt-5 mb-5">
        <Row className="mb-4 animate__animated animate__fadeIn">
          <Col>
            <h2
              className="text-center text-light"
              style={{
                fontFamily: "'blowbrush', sans-serif",
                letterSpacing: '1px',
                fontSize: '3.5rem',
              }}
            >
              NOTIFICATION SETTINGS
            </h2>
          </Col>
        </Row>
        <Row className="animate__animated animate__fadeIn">
          <Col>
            <Card className="card-custom">
              <Card.Header
                as="h3"
                style={{
                  background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)',
                  color: 'white',
                  fontFamily: "'Poppins', sans-serif'"
                }}
              >
                Configure Notifications
              </Card.Header>
              <Card.Body>
                {/* Create New Setting */}
                <h5>Add New Notification</h5>
                <Form className="mb-4">
                  <Row className="g-2">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Action</Form.Label>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="outline-primary"
                            className="w-100 text-left"
                          >
                            {newAction
                              ? availableActions.find(
                                  (a) => a.actionName === newAction
                                )?.displayName || 'Select Action'
                              : 'Select Action'}
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="w-100">
                            {availableActions.map((action) => (
                              <Dropdown.Item
                                key={action.actionName}
                                onClick={() => setNewAction(action.actionName)}
                              >
                                {action.displayName}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Message Template</Form.Label>
                        <Form.Control
                          type="text"
                          value={newMessageTemplate}
                          onChange={(e) =>
                            setNewMessageTemplate(e.target.value)
                          }
                          placeholder="Enter message (use {Name} for name)"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Form.Group>
                        <Form.Check
                          type="checkbox"
                          label="Enabled"
                          checked={newIsEnabled}
                          onChange={(e) =>
                            setNewIsEnabled(e.target.checked)
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button
                    className="btn-custom-primary mt-3"
                    onClick={handleCreate}
                    disabled={!newAction || !newMessageTemplate}
                  >
                    Add Notification
                  </Button>
                </Form>

                {/* Existing Settings */}
                <h5>Existing Notifications</h5>
                <Table striped bordered hover className="table-custom">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Message Template</th>
                      <th>Enabled</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.map((setting, index) => (
                      <tr
                        key={setting.id}
                        className="animate__animated animate__slideInUp"
                        style={{ animationDelay: `${0.1 * index}s` }}
                      >
                        <td>{setting.displayName}</td>
                        <td>
                          <Form.Control
                            type="text"
                            value={setting.messageTemplate}
                            onChange={(e) =>
                              setSettings(
                                settings.map((s) =>
                                  s.id === setting.id
                                    ? {
                                        ...s,
                                        messageTemplate: e.target.value,
                                      }
                                    : s
                                )
                              )
                            }
                            placeholder="Enter message (use {Name} for name)"
                          />
                        </td>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={setting.isEnabled}
                            onChange={(e) =>
                              setSettings(
                                settings.map((s) =>
                                  s.id === setting.id
                                    ? { ...s, isEnabled: e.target.checked }
                                    : s
                                )
                              )
                            }
                          />
                        </td>
                        <td>
                          <Button
                            className="btn-custom-primary me-2"
                            onClick={() =>
                              handleUpdate(
                                setting.id,
                                setting.messageTemplate,
                                setting.isEnabled
                              )
                            }
                          >
                            Save
                          </Button>
                          <Button
                            className="btn-custom-danger"
                            onClick={() => handleDelete(setting.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default NotificationSettings;